/**
 * resultMetadata 전용 성과 블록 존재 여부, 평가 요약 통계 파싱·보완 (UI 없음).
 * 통계 카드 UI는 EvalStatCard / EvalSummaryStatGrid를 사용한다.
 */

import {
  isLsCoreNursingProcedureContentCode,
  type ResultViewType,
} from '@/constants/scenarioTypes';
import type { TriageEvaluationResultDto } from '@/types/api/scenarioResult';
import { isScenarioAnswerCorrect } from '@/utils/scenarioResultFormat';

export interface EvalSummaryStats {
  score?: string | number | null;
  /** EvalStatCard `sub` (예: `/ 100`, `%`, `점`) */
  scoreSub?: string | null;
  correctCount?: string | number | null;
  total?: string | number | null;
  duration?: string | number | null;
}

export type ProcedureSummaryResultType =
  | 'iv'
  | 'blood_draw'
  | 'acls'
  | 'resp_infect'
  | 'competency'
  | 'disease_care'
  | 'ppe'
  | 'fallback';

function isEmpty(v: unknown): boolean {
  return v == null || v === '';
}

function pickString(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'number' && !Number.isNaN(v)) return String(v);
  if (typeof v === 'string') return v.trim() === '' ? undefined : v;
  return undefined;
}

export function hasSpecialtyPerformanceData(resultMetadata: unknown): boolean {
  const m = resultMetadata as Record<string, unknown> | null | undefined;
  if (m == null || typeof m !== 'object') return false;

  const len = (v: unknown): number => (Array.isArray(v) ? v.length : 0);

  if (len(m.gcsPerformance) > 0) return true;
  if (len(m.pupilReactionPerformance) > 0) return true;
  if (len(m.motorPowerPerformance) > 0) return true;
  if (len(m.ivPerformance) > 0) return true;
  if (len(m.ppePerformance) > 0) return true;
  if (len(m.bloodDrawPerformance) > 0) return true;

  if ((len(m.aclsItemRates) + len(m.aclsPerformance)) > 0) return true;
  if ((len(m.respInfectItemRates) + len(m.respInfectPerformance)) > 0) return true;
  if ((len(m.competencyItemRates) + len(m.competencyPerformance)) > 0) return true;
  if ((len(m.diseaseCareItemRates) + len(m.diseaseCarePerformance)) > 0) return true;

  return false;
}

/**
 * `ResultBody`에서 `EvaluationTable` 폴백 표시 여부.
 * LS 핵심간호: IV·전용 블록 데이터가 있으면 숨기고, 플랫폼 체크리스트만 있으면 표시.
 */
export function shouldShowEvaluationChecklistFallback(input: {
  resultMetadata: unknown;
  resultTableView: ResultViewType;
  contentCode?: string | null;
  /** 플랫폼 `scenario-ls` 등에서 채운 수행 행 — LS는 IV 전용 블록이 없을 때 표에만 표시 */
  apiChecklistCount?: number;
}): boolean {
  const { resultMetadata, resultTableView, contentCode, apiChecklistCount } = input;
  if (isLsCoreNursingProcedureContentCode(contentCode ?? null)) {
    if (hasSpecialtyPerformanceData(resultMetadata)) return false;
    if ((apiChecklistCount ?? 0) > 0) return true;
    return false;
  }
  if (hasSpecialtyPerformanceData(resultMetadata)) return false;
  const triagePatients = (resultMetadata as Record<string, unknown> | null | undefined)?.triagePatients;
  const hasTriageRows = Array.isArray(triagePatients) && triagePatients.length > 0;
  return resultTableView !== 'TriageView' || !hasTriageRows;
}

/** `resultMetadata.stats` 레거시 배열 또는 `evaluationSummary` 객체에서 공통 요약 추출 */
export function parseEvalSummaryFromMetadata(meta: unknown): EvalSummaryStats {
  if (meta == null || typeof meta !== 'object') return {};
  const m = meta as Record<string, unknown>;

  // ACLS 전체 참가자: lab_stat 데이터 처리
  const labStat = m.lab_stat;
  if (labStat != null && typeof labStat === 'object') {
    const ls = labStat as Record<string, unknown>;
    const avgScore = ls.avg_score;
    const avgPlayTime = ls.avg_play_time;
    const userCount = ls.user_count ?? ls.studied_user_count;
    
    // avg_play_time을 mm:ss 형식으로 변환
    let durationStr: string | undefined;
    if (typeof avgPlayTime === 'number' && avgPlayTime > 0) {
      const minutes = Math.floor(avgPlayTime / 60);
      const seconds = avgPlayTime % 60;
      durationStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    
    return {
      score: avgScore != null ? String(avgScore) : undefined,
      scoreSub: '%',
      total: userCount != null ? String(userCount) : undefined,
      duration: durationStr,
    };
  }

  const direct = m.evaluationSummary ?? m.evalSummary;
  if (direct != null && typeof direct === 'object') {
    const d = direct as Record<string, unknown>;
    return {
      score: pickString(d.score ?? d.Score),
      scoreSub: pickString(d.scoreSub),
      correctCount: d.correctCount != null ? pickString(d.correctCount) : pickString(d.correct_count),
      total: d.total != null ? pickString(d.total) : pickString(d.totalCount ?? d.total_count),
      duration: pickString(d.duration ?? d.elapsedTime ?? d.elapsed_time),
    };
  }

  const stats = m.stats;
  if (!Array.isArray(stats)) return {};

  const out: EvalSummaryStats = {};
  for (const raw of stats) {
    if (raw == null || typeof raw !== 'object') continue;
    const item = raw as { label?: unknown; value?: unknown; sub?: unknown };
    const label = pickString(item.label) ?? '';
    const value = item.value;
    const sub = pickString(item.sub);

    if (label.includes('정답수') || (label.includes('정답') && label.includes('전체'))) {
      out.correctCount = pickString(value);
      if (sub != null) {
        const totalPart = sub.replace(/^\s*\/\s*/, '').trim();
        if (totalPart !== '') out.total = totalPart;
      }
    } else if (label.includes('소요')) {
      out.duration = [pickString(value), sub].filter(Boolean).join(' ').trim() || pickString(value);
    } else if (
      label.includes('점수')
      || label.includes('획득')
      || label.includes('정답율')
      || label.toLowerCase().includes('score')
    ) {
      out.score = pickString(value) ?? (typeof value === 'number' ? String(value) : undefined);
      if (sub != null && sub !== '') out.scoreSub = sub;
    }
  }
  return out;
}

/**
 * 평가결과 화면: 로컬 시나리오 메타(`scenarioDetails` 등)와 플랫폼 API `resultMetadata` 병합.
 * `stats` / `tables` 는 **API 항목이 동일 라벨이면 시트(base)보다 우선**한다.
 */
export function mergeSimulationDisplayMetadata(
	base: Record<string, unknown> | null | undefined,
	api: Record<string, unknown> | null | undefined
): Record<string, unknown> {
	const b = base && typeof base === 'object' ? base : {};
	const a = api && typeof api === 'object' ? api : {};

	const statKey = (item: unknown): string => {
		const L = (item as { label?: string })?.label;
		return typeof L === 'string' ? L.trim() : '';
	};
	const tableKey = (item: unknown): string => {
		const T = (item as { title?: string })?.title;
		return typeof T === 'string' ? T.trim() : '';
	};

	const baseStats = Array.isArray(b.stats) ? b.stats : [];
	const apiStats = Array.isArray(a.stats) ? a.stats : [];
	const byStat = new Map<string, unknown>();
	/** API 우선 — 시트 정적 stats가 플랫폼 실제 점수·정답률을 덮어쓰지 않게 */
	for (const x of apiStats) {
		const k = statKey(x);
		if (k) byStat.set(k, x);
	}
	for (const x of baseStats) {
		const k = statKey(x);
		if (k && !byStat.has(k)) byStat.set(k, x);
	}

	const baseTables = Array.isArray(b.tables) ? b.tables : [];
	const apiTables = Array.isArray(a.tables) ? a.tables : [];
	const byTable = new Map<string, unknown>();
	for (const x of apiTables) {
		const k = tableKey(x);
		if (k) byTable.set(k, x);
	}
	for (const x of baseTables) {
		const k = tableKey(x);
		if (k && !byTable.has(k)) byTable.set(k, x);
	}

	return {
		...b,
		...a,
		stats: Array.from(byStat.values()),
		tables: Array.from(byTable.values()),
	};
}

export function mergeEvalSummary(primary: EvalSummaryStats, fallback: EvalSummaryStats): EvalSummaryStats {
  const out: EvalSummaryStats = { ...primary };
  const fill = (k: keyof EvalSummaryStats) => {
    if (isEmpty(out[k])) {
      const v = fallback[k];
      if (!isEmpty(v)) (out as Record<string, unknown>)[k as string] = v;
    }
  };
  fill('score');
  fill('scoreSub');
  fill('correctCount');
  fill('total');
  fill('duration');
  return out;
}

/** 신경평가(GCS·동공·근력) 메타: 레거시 assessmentStats/gcsStats + 공통 파싱 — SimulationResults에서 buildSimulationSummary와 병합 */
export function buildAssessmentEvalSummary(resultMetadata: unknown): EvalSummaryStats {
  const m = resultMetadata as Record<string, unknown> | null | undefined;
  const legacyAssessment = m?.assessmentStats ?? m?.gcsStats;
  const fromProps: EvalSummaryStats = {};
  if (legacyAssessment && typeof legacyAssessment === 'object') {
    const a = legacyAssessment as Record<string, unknown>;
    if (a.score != null) fromProps.score = a.score as string | number;
    if (a.scoreSub != null) fromProps.scoreSub = String(a.scoreSub);
    if (a.correctCount != null) fromProps.correctCount = a.correctCount as string | number;
    if (a.total != null) fromProps.total = a.total as string | number;
    if (a.duration != null) fromProps.duration = a.duration as string | number;
  }
  return mergeEvalSummary(parseEvalSummaryFromMetadata(resultMetadata), fromProps);
}

function rowIsCorrectLike(row: any, resultType: ProcedureSummaryResultType): boolean {
  if (resultType === 'resp_infect' && row?.correctRate != null) {
    const n = Number(row.correctRate);
    return !Number.isNaN(n) && n >= 100;
  }
  const r = row?.result;
  if (r == null || r === '') return false;
  if (resultType === 'competency' || resultType === 'disease_care') {
    const s = String(r);
    return s.includes('완료') || s.toLowerCase() === 'completed';
  }
  return isScenarioAnswerCorrect(r);
}

/** 통계·수행결과 카드와 동일: 정답 문자열이 비어 있지 않고 사용자 답과 일치하면 정답 */
export function isTriageResponseCorrect(correctAnswer: unknown, userAnswer: unknown): boolean {
  const c = String(correctAnswer ?? '').trim();
  const u = String(userAnswer ?? '').trim();
  return c !== '' && c === u;
}

function countTriagePatientMatches(patients: unknown[] | undefined): { correct: number; total: number } | null {
  if (!Array.isArray(patients) || patients.length === 0) return null;
  const total = patients.length;
  const correct = patients.filter((p) => {
    const row = p as { correctAnswer?: unknown; userAnswer?: unknown };
    return isTriageResponseCorrect(row?.correctAnswer, row?.userAnswer);
  }).length;
  return { correct, total };
}

/** triagePatients만으로 정답율·정답수/전체 (소요시간은 메타 stats에서 병합) */
export function deriveTriageEvalSummaryFromPatients(patients: unknown[] | undefined): EvalSummaryStats {
  const counted = countTriagePatientMatches(patients);
  if (!counted) return {};
  const { correct, total } = counted;
  const rate = total > 0 ? Math.round((correct / total) * 100) : undefined;
  return {
    score: rate,
    scoreSub: rate != null ? '%' : undefined,
    correctCount: correct,
    total,
  };
}

/** 중증도분류 API DTO → EvalSummaryStatGrid용 — buildSimulationSummary와 동일 규칙 */
export function buildEvalSummaryFromTriageDto(data: TriageEvaluationResultDto | null | undefined): EvalSummaryStats {
  if (data == null) return {};
  const stats = data.stats;
  const patients = data.patients ?? [];
  const totalForStats = stats?.totalCount ?? (patients.length > 0 ? patients.length : undefined);
  const counted = countTriagePatientMatches(patients);
  const derivedCorrectCount = stats?.correctCount ?? (counted != null ? counted.correct : undefined);
  let correctRatePct = stats?.correctRate;
  if (correctRatePct == null && totalForStats != null && totalForStats > 0 && derivedCorrectCount != null) {
    correctRatePct = Math.round((derivedCorrectCount / totalForStats) * 100);
  }
  const hasCorrectPair = derivedCorrectCount != null && totalForStats != null && totalForStats > 0;
  return {
    score: correctRatePct != null ? correctRatePct : undefined,
    scoreSub: correctRatePct != null ? '%' : undefined,
    correctCount: hasCorrectPair ? derivedCorrectCount : undefined,
    total: hasCorrectPair ? totalForStats : undefined,
    duration: stats?.duration != null && stats.duration !== '' ? stats.duration : undefined,
  };
}

/** 클래스 세션 상단 카드: triagePatients 있으면 그 기준 정답율·정답수/전체, 없으면 메타 stats 파싱 */
export function buildSimulationSummary(meta: unknown): EvalSummaryStats {
  const parsed = parseEvalSummaryFromMetadata(meta);
  const m = meta as Record<string, unknown> | null | undefined;
  const patients = m?.triagePatients;
  const fromPatients = deriveTriageEvalSummaryFromPatients(Array.isArray(patients) ? patients : undefined);

  const hasPatientStats = !isEmpty(fromPatients.correctCount) && !isEmpty(fromPatients.total);
  if (hasPatientStats) {
    return mergeEvalSummary(fromPatients, { duration: parsed.duration });
  }
  return parsed;
}

/** @deprecated buildSimulationSummary 사용 */
export const buildTriageSimulationSummary = buildSimulationSummary;

/** 수행 행만으로 정답율(%)·정답/전체 보완 (메타에 없을 때) */
export function deriveProcedureSummaryFromRows(
  rows: any[],
  resultType: ProcedureSummaryResultType,
): EvalSummaryStats {
  if (!rows?.length) return {};
  const total = rows.length;
  const correctCount = rows.filter((row) => rowIsCorrectLike(row, resultType)).length;
  const rate = total > 0 ? Math.round((correctCount / total) * 100) : undefined;

  return {
    correctCount,
    total,
    ...(rate != null ? { score: rate, scoreSub: '%' as const } : {}),
  };
}
